export const SUPPORTED_LOCALES = ['pt', 'en', 'es', 'fr', 'pl', 'de'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = 'pt';

/**
 * The wall clock every screen reads in, and the answer to "in what timezone?"
 * whenever a caller does not say.
 *
 * It has to be a named zone rather than the runtime's, because the runtime is a
 * Cloud Run container and Cloud Run containers are UTC. Portugal is UTC+1 for
 * half the year, so "no timezone" quietly meant every timestamp in the admin
 * was an hour early from March to October: a 15:07 enquiry read 14:07, and
 * reception had no way to know which one the patient's clock had said.
 *
 * next-intl has been configured with this value since the app was set up
 * (`i18n/request.ts`); the mistake was that `lib/format/datetime.ts` never
 * honoured it and every other subsystem that got it right — reminder copy,
 * email templates, greetings, utilization days — kept its own copy of the
 * string. This is that one copy.
 *
 * Both tenants are Portuguese. When one is not, this becomes a per-tenant
 * setting and `clinics.timezone` (already `not null`) becomes the override
 * callers pass explicitly, exactly as the enquiry screens now do.
 *
 * NOT the same thing as the other `Europe/Lisbon` constants still in the tree,
 * even though they hold the identical string today, so do not merge them:
 * `PORTUGAL_TZ` and `AGENDA_TIMEZONE` are the zone a clinic DAY is bucketed in,
 * and the Novigest adapter's is the zone the upstream API speaks. A tenant that
 * moved to Madrid would change this one and none of those.
 */
export const DEFAULT_TIME_ZONE = 'Europe/Lisbon';

export const localeNames: Record<Locale, string> = {
  pt: 'Português',
  en: 'English',
  es: 'Español',
  fr: 'Français',
  pl: 'Polski',
  de: 'Deutsch',
};

export const localeFlags: Record<Locale, string> = {
  pt: '🇵🇹',
  en: '🇬🇧',
  es: '🇪🇸',
  fr: '🇫🇷',
  pl: '🇵🇱',
  de: '🇩🇪',
};

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}
